import { paths } from "@kwp/firestore";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import {
  browserEnvironment,
  disablePush,
  enablePush,
  pushAvailability,
  PushPermissionError,
  rememberedDeviceId,
  type PushAvailability,
} from "../../lib/push";
import { pushConfig } from "../../lib/runtime-config";
import { ErrorText, PageHeader, Section, StatusText, Switch } from "../../ui";
import { SettingsNav } from "./SettingsNav";

const EXPLAIN: Record<Exclude<PushAvailability, "available">, string> = {
  unconfigured: "Push notifications are not configured for this environment yet, so this device cannot receive them.",
  "ios-needs-install":
    "On iPhone and iPad, notifications work only for KitWork+ added to your Home Screen (iOS 16.4 or later). In Safari tap Share → Add to Home Screen, open KitWork+ from the Home Screen, then turn notifications on here.",
  unsupported: "This browser does not support web push notifications.",
  denied: "Notifications are blocked for this site. Allow them in your browser's site settings, then reload this page.",
};

/** Settings → Notifications: turn web push on or off for this browser or installed app. */
export function NotificationsPage() {
  const { config, firebase } = useClients();
  const me = useMe();
  const push = pushConfig(config);
  const [env, setEnv] = useState(() => browserEnvironment(push !== null));
  const availability = pushAvailability(env);
  const [deviceId, setDeviceId] = useState<string | null>(() => rememberedDeviceId());
  const [registered, setRegistered] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (deviceId === null) {
      setRegistered(false);
      return;
    }
    return onSnapshot(
      doc(firebase.db, paths.userDevice(me.uid, deviceId)),
      (snap) => setRegistered(snap.exists()),
      () => setRegistered(false),
    );
  }, [firebase.db, me.uid, deviceId]);

  const on = registered === true && env.permission === "granted";

  async function toggle(next: boolean) {
    setError(null);
    setStatus(null);
    setBusy(true);
    try {
      if (next) {
        if (push === null) return;
        // Straight from the click: iOS and Safari only prompt after a user gesture.
        const id = await enablePush(firebase, me.uid, push.vapidKey, env.ios && env.standalone ? "ios-pwa" : "web");
        setDeviceId(id);
        setStatus("Notifications are on for this device.");
      } else if (deviceId !== null) {
        await disablePush(firebase, me.uid, deviceId);
        setDeviceId(null);
        setStatus("Notifications are off for this device.");
      }
    } catch (e) {
      if (e instanceof PushPermissionError) setError("Notifications were not allowed, so nothing changed.");
      else setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setEnv(browserEnvironment(push !== null));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Notifications" />
      <SettingsNav />
      <Section
        title="Push notifications"
        description="Get a notification when someone mentions you or sends you a direct message, and when a task is assigned to you. On iPhone and iPad this needs KitWork+ on your Home Screen (iOS 16.4 or later)."
      >
        <div className="flex flex-col gap-3">
          <Switch
            label="Notifications on this device"
            checked={on}
            disabled={busy || registered === null || (availability !== "available" && !on)}
            onChange={(next) => void toggle(next)}
          />
          {availability === "available" ? null : <p className="text-sm text-muted">{EXPLAIN[availability]}</p>}
          {error === null ? null : <ErrorText>{error}</ErrorText>}
          {status === null ? null : <StatusText>{status}</StatusText>}
        </div>
      </Section>
    </div>
  );
}
